import React, { useState, useEffect } from "react";
import {
  AcmeCoOperationErrorIcon,
  AcmeCoOperationErrorIcon1,
} from "../../assets/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { reqToUpdateSettings } from "../../redux/slices.js/settings/settingsSlice";
import { useDispatch } from "react-redux";

const SandboxMode = ({ settings = [] }) => {
  const dispatch = useDispatch();
  // Map API field names to toggle IDs
  const nameToIdMap = {
    enable_sandbox_mode_globally: "esmg",
    allow_test_payments: "atpp",
    enable_mock_payments: "emps",
    enable_webhooks_testing_tool: "ewtt",
  };

  // Reverse map (IDs → API names)
  const idToNameMap = Object.fromEntries(
    Object.entries(nameToIdMap).map(([k, v]) => [v, k])
  );

  // Initialize state from API
  const initialState = settings.reduce(
    (acc, s) => {
      const id = nameToIdMap[s.name];
      if (id) acc[id] = s.value === true || s.value === "true"; // ensure boolean
      return acc;
    },
    {
      esmg: false,
      atpp: false,
      emps: false,
      ewtt: false,
    }
  );

  const [formValues, setFormValues] = useState(initialState);

  const coreSandboxFeaturesToggles = [
    { id: "esmg", label: "Enable sandbox mode globally" },
    { id: "atpp", label: "Allow test payment processing" },
    { id: "emps", label: "Enable mock payment scenarios" },
  ];

  const advancedTestingToggles = [
    { id: "ewtt", label: "Enable webhook testing tools" },
  ];

  const handleToggle = (id) => {
    setFormValues((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSaveChanges = () => {
    // Convert internal IDs back to API field names
    const payload = Object.entries(formValues).map(([id, value]) => ({
      name: idToNameMap[id], // use API field name
      value: value,
    }));

    dispatch(
      reqToUpdateSettings({
        data: payload,
        onSuccess: (response) => {},
        onFailure: (error) => {
          console.error("Failed to update settings:", error);
        },
      })
    );
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-[#EDF7FF] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Development & Testing Environment
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Configure testing capabilities and sandbox features for
                  development and demos
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Core Sandbox Features */}
        <div className="form-group">
          <label className="!text-black pb-2">Core Sandbox Features</label>
          <div className="grid gap-4">
            {coreSandboxFeaturesToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Advanced Testing */}
        <div className="form-group">
          <label className="!text-black pb-2">Advanced Testing</label>
          <div className="grid gap-4">
            {advancedTestingToggles.map((toggle) => (
              <div
                key={toggle.id}
                className="flex items-center justify-start gap-4"
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={formValues[toggle.id]}
                  onClick={() => handleToggle(toggle.id)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    formValues[toggle.id] ? "bg-[#4285F4]" : "bg-[#E5E7EB]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formValues[toggle.id]
                        ? "translate-x-[10px]"
                        : "translate-x-0"
                    }`}
                  />
                </button>
                <span className="text-black text-span">{toggle.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-[#FFF6F1] rounded-xl sm:p-6 p-3">
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between">
            <div className="flex items-center gap-3">
              <span className="min-[40px]">{AcmeCoOperationErrorIcon1}</span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Branding Guidelines
                </span>
                <p className="text-[16px] text-gray-500 text-sm mb-0">
                  Maintain brand consistency while allowing appropriate tenant
                  customization. White-labeling and custom domains are typically
                  reserved for enterprise customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className={`mt-auto flex gap-3 pt-6 justify-end flex-wrap`}>
        <button type="button" className="next-btn" onClick={handleSaveChanges}>
          Save sandbox configuration
        </button>
      </div>
    </>
  );
};

export default SandboxMode;
